import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  AccountCircleOutlined,
  KeyboardArrowDownOutlined,
  CheckOutlined,
  ClearOutlined,
  PeopleOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import './AdminHierarchyFilter.css';

const AdminHierarchyFilter = ({
  admins = [],
  selectedAdminId = null,
  onSelect,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const containerRef = useRef(null);
  const searchRef = useRef(null);

  // Fermer sur clic extérieur
  useEffect(() => {
    const handler = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handler);

    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, []);

  // Focus recherche à l'ouverture
  useEffect(() => {
    if (open && searchRef.current) {
      const timer = setTimeout(() => {
        searchRef.current?.focus();
      }, 60);

      return () => clearTimeout(timer);
    }
  }, [open]);

  // Admin sélectionné
  const selectedAdmin = useMemo(
    () =>
      admins.find(
        (admin) => admin.id === selectedAdminId
      ) || null,
    [admins, selectedAdminId]
  );

  // Filtrage par recherche
  const filteredAdmins = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return admins;
    }

    return admins.filter((admin) => {
      return (
        admin.username?.toLowerCase().includes(query) ||
        admin.name?.toLowerCase().includes(query) ||
        admin.lastname?.toLowerCase().includes(query)
      );
    });
  }, [admins, search]);

  // Initiales
  const getInitials = (admin) => {
    if (!admin) {
      return '?';
    }

    const initials = `${admin.name?.[0] || ''}${
      admin.lastname?.[0] || ''
    }`.toUpperCase();

    return (
      initials ||
      admin.username?.[0]?.toUpperCase() ||
      '?'
    );
  };

  // Sélection sécurisée
  const safeSelect = (id) => {
    if (typeof onSelect !== 'function') {
      console.error(
        '[AdminHierarchyFilter] onSelect prop is not a function.',
        'Received:',
        onSelect,
        '\nVérifier que SecufireActionLogs reçoit bien "onAdminSelect" et le passe en prop "onSelect".'
      );

      return;
    }

    // Re-cliquer sur l'admin sélectionné = désélectionner
    onSelect(
      id === selectedAdminId ? null : id
    );

    setOpen(false);
    setSearch('');
  };

  // Réinitialiser le filtre
  const handleClear = (e) => {
    e.stopPropagation();

    if (typeof onSelect === 'function') {
      onSelect(null);
    }

    setOpen(false);
    setSearch('');
  };

  // Accessibilité du bouton clear
  const handleClearKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClear(e);
    }
  };

  const isActive =
    selectedAdminId !== null &&
    selectedAdminId !== undefined;

  return (
    <div
      className="ahf-wrap"
      ref={containerRef}
    >
      {/* Trigger */}
      <button
        type="button"
        className={`ahf-trigger ${
          isActive ? 'ahf-trigger--active' : ''
        } ${
          open ? 'ahf-trigger--open' : ''
        }`}
        onClick={() => {
          if (!loading) {
            setOpen((value) => !value);
          }
        }}
        title="Filtrer par admin"
        disabled={loading}
      >
        {/* Icon */}
        <div
          className={`ahf-trigger-icon ${
            isActive
              ? 'ahf-trigger-icon--active'
              : ''
          }`}
        >
          {isActive ? (
            <span className="ahf-trigger-initials">
              {getInitials(selectedAdmin)}
            </span>
          ) : (
            <PeopleOutlined
              style={{ fontSize: 14 }}
            />
          )}
        </div>

        {/* Label */}
        <span className="ahf-trigger-label">
          {isActive
            ? selectedAdmin?.username ||
              selectedAdmin?.name ||
              'Admin'
            : 'Tous les admins'}
        </span>

        {/* Clear / Chevron */}
        {isActive ? (
          <span
            className="ahf-clear-btn"
            onClick={handleClear}
            onKeyDown={handleClearKeyDown}
            title="Réinitialiser"
            role="button"
            tabIndex={0}
          >
            <ClearOutlined
              style={{ fontSize: 12 }}
            />
          </span>
        ) : (
          <KeyboardArrowDownOutlined
            className={`ahf-chevron ${
              open ? 'ahf-chevron--up' : ''
            }`}
            style={{ fontSize: 14 }}
          />
        )}

        {/* Active indicator */}
        {isActive && (
          <span className="ahf-active-bar" />
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="ahf-dropdown">
          {/* Header */}
          <div className="ahf-dropdown-header">
            <span className="ahf-dropdown-title">
              <AccountCircleOutlined
                style={{ fontSize: 14 }}
              />

              <span>Filtrer par admin</span>
            </span>

            <span className="ahf-dropdown-count">
              {admins.length} admin
              {admins.length > 1 ? 's' : ''}
            </span>
          </div>

          {/* Search */}
          {admins.length > 5 && (
            <div className="ahf-search">
              <SearchOutlined
                style={{ fontSize: 14 }}
              />

              <input
                ref={searchRef}
                type="text"
                placeholder="Rechercher…"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                className="ahf-search-input"
              />

              {search && (
                <button
                  type="button"
                  className="ahf-search-clear"
                  onClick={() => setSearch('')}
                  title="Effacer la recherche"
                >
                  <ClearOutlined
                    style={{ fontSize: 11 }}
                  />
                </button>
              )}
            </div>
          )}

          {/* Tous les admins */}
          <div
            className={`ahf-option ahf-option--all ${
              !isActive
                ? 'ahf-option--selected'
                : ''
            }`}
            onClick={() => safeSelect(null)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' ||
                e.key === ' '
              ) {
                e.preventDefault();
                safeSelect(null);
              }
            }}
          >
            <div className="ahf-option-avatar ahf-option-avatar--all">
              <PeopleOutlined
                style={{ fontSize: 14 }}
              />
            </div>

            <span className="ahf-option-name">
              Tous les admins
            </span>

            {!isActive && (
              <CheckOutlined
                className="ahf-option-check"
                style={{ fontSize: 13 }}
              />
            )}
          </div>

          <div className="ahf-separator" />

          {/* Admin list */}
          <div className="ahf-list">
            {filteredAdmins.length === 0 && (
              <div className="ahf-no-result">
                Aucun admin trouvé
              </div>
            )}

            {filteredAdmins.map((admin) => {
              const isSelected =
                admin.id === selectedAdminId;

              return (
                <div
                  key={admin.id}
                  className={`ahf-option ${
                    isSelected
                      ? 'ahf-option--selected'
                      : ''
                  } ${
                    !admin.active
                      ? 'ahf-option--inactive'
                      : ''
                  }`}
                  onClick={() =>
                    safeSelect(admin.id)
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Enter' ||
                      e.key === ' '
                    ) {
                      e.preventDefault();
                      safeSelect(admin.id);
                    }
                  }}
                >
                  {/* Avatar */}
                  <div
                    className={`ahf-option-avatar ${
                      admin.role || ''
                    }`}
                  >
                    {getInitials(admin)}
                  </div>

                  {/* Information */}
                  <div className="ahf-option-info">
                    <span className="ahf-option-name">
                      {admin.username}
                    </span>

                    {(admin.name ||
                      admin.lastname) && (
                      <span className="ahf-option-sub">
                        {admin.name || ''}{' '}
                        {admin.lastname || ''}
                      </span>
                    )}
                  </div>

                  {/* Right */}
                  <div className="ahf-option-right">
                    <span
                      className={`ahf-role-dot ${
                        admin.role || ''
                      }`}
                      title={admin.role}
                    />

                    {isSelected && (
                      <CheckOutlined
                        className="ahf-option-check"
                        style={{
                          fontSize: 13,
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHierarchyFilter;